import React, { useState, useEffect } from 'react';
import {
    Edit,
    TabbedForm,
    FormTab,
    BooleanInput,
    SelectInput,
    TextField,
    TextInput,
    NumberInput,
    DateField,
    DateInput,
    LongTextInput,
    ArrayInput,
    SimpleFormIterator,
    required,
    number,
    minValue,
    FormDataConsumer
} from 'react-admin';
import { ACCOUNT_APPROVED_STATUS } from '../constants/account';
import dataProvider from '../dataProvider';
import SearchableSelect from '../common/SearchableSelect';
import { change } from 'redux-form';
import AddressAutoComplete from '../common/AddressAutocomplete';

const validateLimit = [required(), number(), minValue(0)];
const inputStyle = { width: "20em" };
let countrySymbol = '';
// Custom State Select Component
const StateSelect = ({ record, source, label, validate, style }) => {
    const [states, setStates] = useState([]);
    const [loading, setLoading] = useState(false);


    useEffect(() => {
        const fetchStates = async () => {
            if (!record || !countrySymbol) {
                setStates([]);
                return;
            }
            setLoading(true);
            try {
                const response = await dataProvider('GET_LIST', 'states', {
                    pagination: { page: 1, perPage: 1000 },
                    sort: { field: 'name', order: 'ASC' },
                    filter: { country: countrySymbol }
                });
                setStates(response.data);
            } catch (error) {
                console.error('Failed to fetch states:', error);
                setStates([]);
            } finally {
                setLoading(false);
            }
        };
        fetchStates();
    }, [record]);

    return (
        <FormDataConsumer>
            {({ formData, dispatch, scopedFormData, getSource }) => {
                // In ArrayInput, scopedFormData is the current item data, getSource provides the correct source path
                const actualSource = getSource ? getSource('address.state') : source;
                const currentData = scopedFormData || (formData && formData.locations && formData.locations[0]);
                const value = currentData && currentData.address && currentData.address.state 
                    ? currentData.address.state 
                    : '';
                
                return (
                    <div style={style}>
                        <SearchableSelect
                            value={value}
                            onChange={(e) => {
                                dispatch(change('record-form', actualSource, e.target.value));
                            }}
                            options={states}
                            label={label}
                            disabled={loading}
                            error={false}
                            helperText={undefined}
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.name}
                            placeholder="Search state..."
                            allowCustom={false}
                        />
                    </div>
                );
            }}
        </FormDataConsumer>
    );
};

// Custom City Select Component
const CitySelect = ({ record, source, label, validate, style }) => {
    const [cities, setCities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedState, setSelectedState] = useState('');

    useEffect(() => {
        const fetchCities = async () => {
            if (!record || !countrySymbol || !selectedState) {
                setCities([]);
                return;
            }

            setLoading(true);
            try {
                const statesResponse = await dataProvider('GET_LIST', 'states', {
                    pagination: { page: 1, perPage: 1000 },
                    sort: { field: 'name', order: 'ASC' },
                    filter: { country: countrySymbol }
                });
                const state = statesResponse.data.find(s => s.name === selectedState);
                if (state) {
                    const response = await dataProvider('GET_LIST', `cities/state/${state.id}`, {
                        pagination: { page: 1, perPage: 1000 },
                        sort: { field: 'name', order: 'ASC' },
                        filter: { country: countrySymbol }
                    });
                    setCities(response.data);
                } else {
                    setCities([]);
                }
            } catch (error) {
                console.error('Failed to fetch cities:', error);
                setCities([]);
            } finally {
                setLoading(false);
            }
        };

        fetchCities();
    }, [selectedState, record]);

    return (
        <FormDataConsumer>
            {({ formData, dispatch, scopedFormData, getSource }) => {
                // In ArrayInput, scopedFormData is the current item data, getSource provides the correct source path
                const actualSource = getSource ? getSource('address.city') : source;
                const currentData = scopedFormData || (formData && formData.locations && formData.locations[0]);
                const currentState = currentData && currentData.address && currentData.address.state;
                
                if (currentState !== selectedState) {
                    setSelectedState(currentState);
                }
                
                const value = currentData && currentData.address && currentData.address.city 
                    ? currentData.address.city 
                    : '';
                
                return (
                    <div style={style}>
                        <SearchableSelect
                            value={value}
                            onChange={(e) => {
                                dispatch(change('record-form', actualSource, e.target.value));
                            }}
                            options={cities}
                            label={label}
                            disabled={!selectedState || loading}
                            error={false}
                            helperText={undefined}
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.name}
                            placeholder="Search city..."
                        />
                    </div>
                );
            }}
        </FormDataConsumer>
    );
};

export const AccountEdit = ({ permissions, ...props }) => {
    return (
        <Edit title="Account" {...props}>
            <TabbedForm>
                <FormTab label="resources.accounts.tabs.identity">
                    <TextInput
                        source="companyName"
                    />
                    <TextInput
                        source="registrationNumber"
                    />
                    <DateInput
                        source="formationDate"
                    />
                    {(permissions === "system_admin" || permissions === "operator") ? [
                        <NumberInput style={inputStyle} source="creditLimit" label="CreditLimit" validate={validateLimit} defaultValue={0} />,
                        <NumberInput style={inputStyle} source="notifyUpperLimit" label="Sell Notify Upper Limit" validate={validateLimit} />
                    ] : null}
                    {((permissions === "system_admin" || permissions === "operator") && !props.approved) ? [
                        <SelectInput label="Approved Status" source="approvedStatus" choices={
                            Object.keys(ACCOUNT_APPROVED_STATUS).map(key => ({
                                id: key,
                                name: ACCOUNT_APPROVED_STATUS[key]
                            }))
                        } />,
                        <LongTextInput label="Notes"  source="notes" />
                    ] : null}
                </FormTab>
                {(permissions === "system_admin" || permissions === "operator") && (
                    <FormTab label="Business Address">
                        {/* <TextInput source="address.address1" label="Address" style={inputStyle} /> */}
                        <TextInput label="Unit Number" source="address.address2"  style={{ inputStyle}} />
                        <TextInput label="Street Address" source="address.address1"   />
                        <TextInput source="address.city" label="City" style={inputStyle} />
                        <TextInput source="address.state" label="State" style={inputStyle} />
                        <TextInput source="address.postalCode" label="Postal Code" style={inputStyle} />
                        <TextInput source="countrySymbol" label="Country" style={inputStyle} disabled />
                    </FormTab>
                )}
                {(permissions === "system_admin" || permissions === "operator") && (
                    <FormTab label="Locations">
                        <ArrayInput source="locations" label="">
                            <SimpleFormIterator disableAdd disableRemove>
                                <TextInput source="nickName" label="Store Name" />
                                {/* <TextInput source="address.address1" label="Street Address" /> */}
                                <TextInput label="Unit Number" source="address.address2"   />
                                <TextInput label="Street Address" source="address.address1"   />
                                <StateSelect source="address.state" label="State"  />
                                <CitySelect source="address.city" label="City" />
                                <TextInput source="address.postalCode" label="Postal Code" />
                            </SimpleFormIterator>
                        </ArrayInput>
                    </FormTab>
                )} 
                <FormTab label="resources.accounts.tabs.settings">
                    <BooleanInput label="Multi factor authentication" source="isMFA" />
                </FormTab>
                <FormDataConsumer>
                    {({ formData }) => {
                        countrySymbol = formData && formData.countrySymbol;
                        return null;
                    }}
                </FormDataConsumer>
            </TabbedForm>
        </Edit>
        );
};

export default AccountEdit;
